import React, { useRef, useState } from "react";
import { Save, X } from "lucide-react";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line, Scatter } from 'react-chartjs-2';
import { generateCashFlowChartData, generateROIChartData, generateSensitivityChartData } from '../utils/chartDataGenerators';
import { downloadPDF } from '../utils/pdfDownloadUtils';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const BusinessValueReport = ({ 
  cashFlowReport, 
  formData, 
  capabilities, 
  expandedUseCases, 
  toggleUseCase,
  businessValueProjectId,
  onClose
}) => {
  const reportRef = useRef(null);

  // Format numbers for display
  const formatCurrency = (value) => {
    const num = parseFloat(value);
    if (isNaN(num)) return '$0';
    
    // Round to avoid floating point precision errors
    const roundedNum = Math.round(num * 1000000) / 1000000; // Round to 6 decimal places
    
    // Check if the number has meaningful decimal places after rounding
    const hasDecimals = Math.abs(roundedNum % 1) > 0.000001; // Only consider decimals if > 0.000001
    
    if (hasDecimals) {
      // If it has meaningful decimals, show them (up to 1 decimal place for currency)
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 1
      }).format(roundedNum);
    } else {
      // If it's effectively a whole number, show it without decimals
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(Math.round(roundedNum));
    }
  };

  const formatPercentage = (value) => {
    if (value === undefined || value === null || isNaN(value)) {
      return '0.0%';
    }
    return `${parseFloat(value).toFixed(1)}%`;
  };

  const formatNumber = (value) => {
    const num = parseFloat(value);
    if (isNaN(num)) return '0';
    
    // Round to avoid floating point precision errors
    const roundedNum = Math.round(num * 1000000) / 1000000; // Round to 6 decimal places
    
    // Check if the number has meaningful decimal places after rounding
    const hasDecimals = Math.abs(roundedNum % 1) > 0.000001; // Only consider decimals if > 0.000001
    
    if (hasDecimals) {
      // If it has meaningful decimals, show them (up to 1 decimal place)
      return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 1
      }).format(roundedNum);
    } else {
      // If it's effectively a whole number, show it without decimals
      return Math.round(roundedNum).toLocaleString('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      });
    }
  };

  // Calculate adoption impact rate string
  const getAdoptionImpactRate = () => {
    const years = parseInt(formData.expectedDurationYear) || 0;
    return formData.adoptionRateOverTime.slice(0, years).map((rate, index) => 
      `Y${index + 1}: ${rate && rate !== "" ? rate : "Not set"}%`
    ).join('; ');
  };

  // Get implementation effort text
  const getImplementationEffortText = (effort) => {
    const effortMap = {
      1: 'Very Low',
      2: 'Low', 
      3: 'Medium',
      4: 'High',
      5: 'Tough'
    };
    return `${effort} (${effortMap[effort] || 'Unknown'})`;
  };

  // Generate capabilities display from selectedCapabilities
  const getCapabilitiesDisplay = () => {
    if (formData.selectedCapabilities && Array.isArray(formData.selectedCapabilities) && formData.selectedCapabilities.length > 0) {
      // Process the nested structure
      const groupedCapabilities = {};
      formData.selectedCapabilities.forEach(capabilityItem => {
        const capability = capabilities.find(cap => cap.id === capabilityItem.capabilityId);
        if (capability) {
          if (!groupedCapabilities[capability.capabilityName]) {
            groupedCapabilities[capability.capabilityName] = [];
          }
          // Process selectedSubCapabilities array
          capabilityItem.selectedSubCapabilities?.forEach(subItem => {
            const subCapability = capability.subCapabilities?.find(sub => sub.subId === subItem.subCapabilityId);
            if (subCapability) {
              groupedCapabilities[capability.capabilityName].push(subCapability.subCapabilityName);
            }
          });
        }
      });
      
      // Format the display
      return Object.entries(groupedCapabilities).map(([capabilityName, subCapabilities]) => {
        return `${capabilityName}: ${subCapabilities.join(', ')}`;
      }).join('; ');
    } else if (formData.dataAICapabilities && Array.isArray(formData.dataAICapabilities) && formData.dataAICapabilities.length > 0) {
      return formData.dataAICapabilities.join(', ');
    }
    return 'No capabilities selected';
  };

  const dataAICapabilities = getCapabilitiesDisplay();

  if (!cashFlowReport) {
    return (
      <div className="business-value-report">
        <div className="report-header">
          <h1>Loading Report...</h1>
        </div>
      </div>
    );
  }

  return (
    <div className="business-value-report" ref={reportRef}>
      {/* Report Title Header */}
      <div className="report-header">
        <div className="report-actions">
          <button 
            className="btn btn-primary"
            onClick={() => downloadPDF(reportRef, formData)}
          >
            <Save /> Download PDF
          </button>
          <button 
            className="btn btn-secondary"
            onClick={onClose}
          >
            <X /> Cancel
          </button>
        </div>
      </div>
      <div className="calculator-header">
        <h2 className="calculator-header-title">Project Business Value Report</h2>
      </div>
      
      <div className="report-content">
        {/* Project Details and Use Case Side by Side */}
        <div className="report-section project-header-section">
          <div className="project-details-section">
            {/* Project Title */}
            <div className="project-detail-item">
              <div className="section-label">Project Title:</div>
              <div className="section-content">{formData.projectName || 'N/A'}</div>
            </div>

            {/* Project Description */}
            <div className="project-detail-item">
              <div className="section-label">Project Description:</div>
              <div className="section-content">{formData.projectDescription || 'No description provided.'}</div>
            </div>

            {/* Project Analysis */}
            <div className="project-detail-item">
              <div className="section-label">Project Analysis:</div>
              <div className="section-content">
                {cashFlowReport.businessValueReport?.project_analysis?.["Project Analysis"] || 'No project analysis available.'}
              </div>
            </div>

            {/* Project Estimations */}
            <div className="project-detail-item">
              <div className="section-label">Project Estimations:</div>
              <div className="section-content">{cashFlowReport.businessValueReport?.explanations?.["Project Estimations"] || 'No project estimations available.'}</div>
            </div>
          </div>
            
          <div className="use-case-section">
            <div className="use-case-header">
              <span className="use-case-label">Use Cases</span>
            </div>
            <div className="use-case-content">
              {cashFlowReport.businessValueReport?.use_cases && cashFlowReport.businessValueReport.use_cases.length > 0 ? (
                cashFlowReport.businessValueReport.use_cases.map((useCaseGroup, groupIndex) => (
                  useCaseGroup.use_cases.map((useCase, useCaseIndex) => {
                    const useCaseKey = `${groupIndex}-${useCaseIndex}`;
                    const isExpanded = expandedUseCases[useCaseKey] || false;
                    return (
                      <div key={useCaseIndex} className="individual-use-case-box">
                        <div 
                          className="individual-use-case-header" 
                          onClick={() => toggleUseCase(useCaseKey)}
                        >
                          <div className="individual-use-case-title">
                            <span className="individual-use-case-icon">
                              {isExpanded ? '−' : '+'}
                            </span>
                            <span className="individual-use-case-label">
                              {useCase.title}
                            </span>
                          </div>
                        </div>
                        {isExpanded && (
                          <div className="individual-use-case-content">
                            <div className="individual-use-case-details">
                              <p className="use-case-description">{useCase.description}</p>
                              {useCase.source && (
                                <p className="use-case-source">
                                  <strong>Source:</strong> <a href={useCase.source} target="_blank" rel="noopener noreferrer">{useCase.source}</a>
                                </p>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })
                )).flat()
              ) : (
                <div className="no-use-cases">
                  <p>No use cases available for this project.</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Cash Flow Analysis */}
        <div className="report-section">
          <div className="section-label">Cash Flow Analysis:</div>
          <div className="section-content">{cashFlowReport.businessValueReport?.explanations?.["Cash Flow Analysis"] || 'No cash flow analysis available.'}</div>
        </div>

        {/* Cash Flow Analysis Table */}
        <div className="report-section">
          <div className="section-label">Calculation of each year</div>
          <div className="cash-flow-table-container">
            <table className="cash-flow-table">
              <thead>
                <tr>
                  <th>Year</th>
                  <th>Adoption Rate</th>
                  <th>Implementation Cost</th>
                  <th>Annual Saving</th>
                  <th>Annual Recurring Cost</th>
                  <th>Net Cash Flow</th>
                </tr>
              </thead>
              <tbody>
                {cashFlowReport.yearwiseCashFlows && cashFlowReport.yearwiseCashFlows.length > 0 ? (
                  cashFlowReport.yearwiseCashFlows.map((yearData, index) => (
                    <tr key={index} className={yearData.year === 0 ? 'implementation-row' : ''}>
                      <td>{yearData.year === 0 ? 'Year 0' : `Year ${yearData.year}`}</td>
                      <td>{formatPercentage(yearData.adoptionRate)}</td>
                      <td>{yearData.year === 0 ? formatCurrency(yearData.cashOutflows) : '$0'}</td>
                      <td>{formatCurrency(yearData.cashInflows)}</td>
                      <td>{yearData.year === 0 ? '$0' : formatCurrency(yearData.cashOutflows)}</td>
                      <td className={yearData.netCashFlow >= 0 ? 'positive' : 'negative'}>
                        {formatCurrency(yearData.netCashFlow)}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" style={{ textAlign: 'center', padding: '20px' }}>
                      No cash flow data available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Project Parameters */}
        <div className="report-section">
          <div className="section-label">Project Parameters:</div>
          <div className="parameters-list">
            <div className="parameter-item">
              <span className="parameter-label">Project term:</span>
              <span className="parameter-value">{cashFlowReport.projectTermYears} years</span>
            </div>
            <div className="parameter-item">
              <span className="parameter-label">Implementation cost =</span>
              <span className="parameter-value">{formatCurrency(parseFloat(formData.initialImplementationCost) || 0)}</span>
            </div>
            <div className="parameter-item">
              <span className="parameter-label">Annual financial saving =</span>
              <span className="parameter-value">{formatCurrency(cashFlowReport.totalAnnualSavings)}</span>
            </div>
            <div className="parameter-item">
              <span className="parameter-label">Annual recurring costs =</span>
              <span className="parameter-value">{formatCurrency(parseFloat(formData.annualRecurringCosts) || 0)}</span>
            </div>
            <div className="parameter-item">
              <span className="parameter-label">Adoption impact rate =</span>
              <span className="parameter-value">{getAdoptionImpactRate()}</span>
            </div>
            {/* <div className="parameter-item">
              <span className="parameter-label">Implementation effort =</span>
              <span className="parameter-value">{getImplementationEffortText(formData.implementationEffort)}</span>
            </div> */}
            <div className="parameter-item">
              <span className="parameter-label">Shared impact =</span>
              <span className="parameter-value">{formData.sharedImpact || 0}</span>
            </div>
            <div className="parameter-item">
              <span className="parameter-label">Project discount rate (WACC%) =</span>
              <span className="parameter-value">{formData.discountRate || 0}%</span>
            </div>
          </div>
        </div>

        {/* Key Financial Metrics */}
        <div className="report-section">
          <div className="section-label">Key Financial Metrics Summary:</div>
          <div className="financial-metrics-grid">
            <div className="metric-item">
              <span className="metric-value">{formatCurrency(cashFlowReport.netPresentValue)}</span>
              <span className="metric-label">NPV</span>
            </div>
            <div className="metric-item">
              <span className="metric-value">{formatPercentage(cashFlowReport.internalRateOfReturn)}</span>
              <span className="metric-label">IRR</span>
            </div>
            <div className="metric-item">
              <span className="metric-value">{Math.round(cashFlowReport.paybackPeriodMonths / 12)} Years</span>
              <span className="metric-label">Payback</span>
            </div>
            <div className="metric-item">
              <span className="metric-value">{formatPercentage(cashFlowReport.roiYear1Percentage)}</span>
              <span className="metric-label">ROI</span>
            </div>
          </div>
        </div>

        {/* Detailed Financial Analysis */}
        <div className="report-section">
          <div className="section-label">Net Present Value (NPV):</div>
          <div className="section-content">{cashFlowReport.businessValueReport?.explanations?.["Net Present Value (NPV)"] || `The Net Present Value of ${formatCurrency(cashFlowReport.netPresentValue)} indicates the project's potential to generate positive returns when considering the time value of money and the specified discount rate.`}</div>
        </div>

        <div className="report-section">
          <div className="section-label">Internal Rate of Return (IRR):</div>
          <div className="section-content">{cashFlowReport.businessValueReport?.explanations?.["Internal Rate of Return (IRR)"] || `An IRR of ${formatPercentage(cashFlowReport.internalRateOfReturn)} represents the annualized effective compounded return rate, providing insight into the project's profitability compared to alternative investment opportunities.`}</div>
        </div>

        <div className="report-section">
          <div className="section-label">Payback Period:</div>
          <div className="section-content">{cashFlowReport.businessValueReport?.explanations?.["Payback Period"] || `The payback period of ${Math.round(cashFlowReport.paybackPeriodMonths / 12)} years indicates how long it will take to recover the initial investment through the project's cash flows.`}</div>
        </div>

        <div className="report-section">
          <div className="section-label">Return on Investment (ROI):</div>
          <div className="section-content">{cashFlowReport.businessValueReport?.explanations?.["Return on Investment (ROI)"] || `A ${formatPercentage(cashFlowReport.roiYear1Percentage)} ROI demonstrates the efficiency of the investment, showing the return generated for each dollar invested in the project.`}</div>
        </div>

        {/* Sensitivity Analysis */}
        <div className="report-section">
          <div className="section-label">Sensitivity Analysis:</div>
          <div className="section-content">{cashFlowReport.businessValueReport?.explanations?.["Sensitivity Analysis"] || 'No sensitivity analysis available.'}</div>
        </div>

        {(cashFlowReport.sensitivityAnalysis || cashFlowReport.senstivityAnalysis) && (
          <div className="report-section">
            <div className="section-label">Sensitivity Analysis Details</div>
            <div className="sensitivity-analysis-content">
              {/* Sensitivity Analysis Calculation Table */}
              {cashFlowReport.sensitivityAnalysis.calculationTable && (
                <div className="sensitivity-calculation-table-container">
                  <table className="sensitivity-calculation-table">
                    <thead>
                      <tr>
                        <th rowSpan="2">{cashFlowReport.sensitivityAnalysis.calculationTable.secondParameterName || 'Second Parameter'}</th>
                        <th colSpan="3" style={{ textAlign: 'center' }}>
                          {cashFlowReport.sensitivityAnalysis.calculationTable.firstParameterName || 'First Parameter'}
                        </th>
                      </tr>
                      <tr>
                        {cashFlowReport.sensitivityAnalysis.calculationTable.columnHeaders && cashFlowReport.sensitivityAnalysis.calculationTable.columnHeaders.map((header, index) => (
                          <th key={index}>
                            {formatNumber(header)}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {cashFlowReport.sensitivityAnalysis.calculationTable.rows && cashFlowReport.sensitivityAnalysis.calculationTable.rows.length > 0 ? (
                        cashFlowReport.sensitivityAnalysis.calculationTable.rows.map((row, index) => (
                          <tr key={index}>
                            <td>{formatNumber(row.rowHeaderValue)}</td>
                            {row.leftCell && (
                              <td className={row.leftCell.roi >= 0 ? 'positive' : 'negative'}>
                                <div>ROI: {formatPercentage(row.leftCell.roi)}</div>
                                <div>NPV: {formatCurrency(row.leftCell.npv)}</div>
                              </td>
                            )}
                            {row.middleCell && (
                              <td className={row.middleCell.roi >= 0 ? 'positive' : 'negative'}>
                                <div>ROI: {formatPercentage(row.middleCell.roi)}</div>
                                <div>NPV: {formatCurrency(row.middleCell.npv)}</div>
                              </td>
                            )}
                            {row.rightCell && (
                              <td className={row.rightCell.roi >= 0 ? 'positive' : 'negative'}>
                                <div>ROI: {formatPercentage(row.rightCell.roi)}</div>
                                <div>NPV: {formatCurrency(row.rightCell.npv)}</div>
                              </td>
                            )}
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="4" style={{ textAlign: 'center', padding: '20px' }}>
                            No sensitivity analysis data available
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Project Summary Section */}
        <div className="report-section">
          <div className="section-label">Project Summary:</div>
          <div className="section-content">
            This business value analysis evaluates the financial viability of the <strong>{formData.projectName}</strong> project 
            over a {formData.projectDuration || 5}-year period. The analysis considers implementation costs, 
            annual savings of {formatCurrency(formData.revenueGrowthDerivedSavings || 0)}, 
            recurring costs of {formatCurrency(formData.annualRecurringCost || 0)}, 
            and a discount rate of {formData.discountRate || 0}% to determine the project's financial impact and return potential.
            {formData.roiSensitivityType && formData.roiSensitivityRate && (
              <span>
                <br/><br/>
                <strong>ROI Sensitivity Analysis:</strong> This analysis includes a {formData.roiSensitivityRate}% sensitivity variation applied to the {
                  formData.roiSensitivityType === 'wacc' 
                    ? `discount rate (WACC) to assess the project's resilience to changes in cost of capital.`
                    : `derived savings to evaluate the project's sensitivity to variations in expected benefits.`
                }
              </span>
            )}
          </div>
        </div>

        {/* Charts Section - 2 Column Layout */}
        <div className="report-section charts-grid">
          <div className="chart-card">
            <div className="section-label">Cash Flow Timeline Chart</div>
            <div className="chart-container">
              <Line 
                data={generateCashFlowChartData(cashFlowReport, formData)}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    title: {
                      display: true,
                      text: 'Project Cash Flow Analysis',
                      font: {
                        size: 16,
                        weight: 'bold'
                      },
                      color: '#2c3e50',
                      padding: 15
                    },
                    legend: {
                      position: 'top',
                      labels: {
                        usePointStyle: true,
                        padding: 15,
                        font: {
                          size: 11,
                          weight: 'bold'
                        }
                      }
                    },
                    tooltip: {
                      mode: 'index',
                      intersect: false,
                      backgroundColor: 'rgba(0, 0, 0, 0.8)',
                      titleColor: 'white',
                      bodyColor: 'white',
                      borderColor: '#A72B2A',
                      borderWidth: 2,
                      callbacks: {
                        title: function(context) {
                          return context[0].label;
                        },
                        label: function(context) {
                          const value = context.parsed.y;
                          const formattedValue = Math.abs(value).toLocaleString('en-US', {
                            style: 'currency',
                            currency: 'USD',
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0
                          });
                          return context.dataset.label + ': ' + (value < 0 ? '-' : '') + formattedValue;
                        },
                        afterLabel: function(context) {
                          if (context.dataset.label === 'Cumulative Cash Flow') {
                            const cumulative = context.parsed.y;
                            const breakEven = cumulative >= 0 ? '✓ Break-even achieved' : '⚠ Not yet break-even';
                            return breakEven;
                          }
                          return '';
                        }
                      }
                    }
                  },
                  scales: {
                    x: {
                      display: true,
                      title: {
                        display: true,
                        text: 'Project Timeline (Years)',
                        font: {
                          size: 12,
                          weight: 'bold'
                        },
                        color: '#2c3e50'
                      },
                      grid: {
                        color: 'rgba(0, 0, 0, 0.1)'
                      }
                    },
                    y: {
                      display: true,
                      title: {
                        display: true,
                        text: 'Cash Flow (USD)',
                        font: {
                          size: 12,
                          weight: 'bold'
                        },
                        color: '#2c3e50'
                      },
                      ticks: {
                        callback: function(value) {
                          return '$' + Math.abs(value).toLocaleString();
                        },
                        font: {
                          size: 10
                        }
                      },
                      grid: {
                        color: 'rgba(0, 0, 0, 0.1)'
                      }
                    }
                  },
                  interaction: {
                    mode: 'nearest',
                    axis: 'x',
                    intersect: false
                  },
                  elements: {
                    point: {
                      hoverBorderWidth: 3
                    }
                  }
                }}
              />
            </div>
          </div>

          <div className="chart-card">
            <div className="section-label">ROI Progression Chart</div>
            <div className="chart-container">
              <Line 
                data={generateROIChartData(cashFlowReport, formData)}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    title: {
                      display: true,
                      text: 'ROI Analysis - Value Creation',
                      font: {
                        size: 16,
                        weight: 'bold'
                      },
                      color: '#2c3e50',
                      padding: 15
                    },
                    legend: {
                      position: 'top',
                      labels: {
                        usePointStyle: true,
                        padding: 15,
                        font: {
                          size: 11,
                          weight: 'bold'
                        }
                      }
                    },
                    tooltip: {
                      mode: 'index',
                      intersect: false,
                      backgroundColor: 'rgba(0, 0, 0, 0.8)',
                      titleColor: 'white',
                      bodyColor: 'white',
                      borderColor: '#A72B2A',
                      borderWidth: 2,
                      callbacks: {
                        title: function(context) {
                          return context[0].label;
                        },
                        label: function(context) {
                          const value = context.parsed.y;
                          return context.dataset.label + ': ' + value.toFixed(1) + '%';
                        },
                        afterLabel: function(context) {
                          if (context.dataset.label === 'Cumulative ROI (%)') {
                            const cumulativeROI = context.parsed.y;
                            const performance = cumulativeROI > 50 ? '🚀 Excellent' : 
                                             cumulativeROI > 20 ? '✅ Good' : 
                                             cumulativeROI > 0 ? '⚠️ Moderate' : '❌ Poor';
                            return performance;
                          }
                          if (context.dataset.label.includes('Target ROI')) {
                            const currentROI = context.parsed.y;
                            const targetROI = context.dataset.data[context.dataIndex];
                            const status = currentROI >= targetROI ? '✓ Target achieved' : '⚠ Below target';
                            return status;
                          }
                          return '';
                        }
                      }
                    }
                  },
                  scales: {
                    x: {
                      display: true,
                      title: {
                        display: true,
                        text: 'Project Timeline (Years)',
                        font: {
                          size: 12,
                          weight: 'bold'
                        },
                        color: '#2c3e50'
                      },
                      grid: {
                        color: 'rgba(0, 0, 0, 0.1)'
                      }
                    },
                    y: {
                      display: true,
                      title: {
                        display: true,
                        text: 'ROI (%)',
                        font: {
                          size: 12,
                          weight: 'bold'
                        },
                        color: '#2c3e50'
                      },
                      ticks: {
                        callback: function(value) {
                          return value + '%';
                        },
                        font: {
                          size: 10
                        }
                      },
                      grid: {
                        color: 'rgba(0, 0, 0, 0.1)'
                      }
                    }
                  },
                  interaction: {
                    mode: 'nearest',
                    axis: 'x',
                    intersect: false
                  },
                  elements: {
                    point: {
                      hoverBorderWidth: 3
                    }
                  }
                }}
              />
            </div>
          </div>
        </div>

        {/* Conclusion Section */}
        <div className="report-section">
          <div className="section-label">Conclusion & Recommendations:</div>
          <div className="section-content">
            {cashFlowReport.businessValueReport?.project_analysis?.Recommendations && cashFlowReport.businessValueReport.project_analysis.Recommendations.length > 0 ? (
              <div>
                <p>
                  Based on the comprehensive financial analysis, this project demonstrates 
                  {cashFlowReport.netPresentValue >= 0 ? ' strong financial viability' : ' concerning financial performance'} 
                  with a Net Present Value of {formatCurrency(cashFlowReport.netPresentValue)} and an Internal Rate of Return of {formatPercentage(cashFlowReport.internalRateOfReturn)}.
                </p>
                <p>
                  The {Math.round(cashFlowReport.paybackPeriodMonths / 12)}-year payback period indicates 
                  {Math.round(cashFlowReport.paybackPeriodMonths / 12) <= 3 ? ' rapid' : ' moderate'} 
                  return on investment, making this project 
                  {cashFlowReport.netPresentValue >= 0 ? ' financially attractive' : ' financially challenging'} 
                  for implementation.
                </p>
                <div className="recommendations-section">
                  <h4>Key Recommendations:</h4>
                  <ul className="recommendations-list">
                    {cashFlowReport.businessValueReport.project_analysis.Recommendations.map((recommendation, index) => (
                      <li key={index} className="recommendation-item">
                        {recommendation}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ) : (
              <div>
                <p>
                  Based on the comprehensive financial analysis, this project demonstrates 
                  {cashFlowReport.netPresentValue >= 0 ? ' strong financial viability' : ' concerning financial performance'} 
                  with a Net Present Value of {formatCurrency(cashFlowReport.netPresentValue)} and an Internal Rate of Return of {formatPercentage(cashFlowReport.internalRateOfReturn)}.
                </p>
                <p>
                  The {Math.round(cashFlowReport.paybackPeriodMonths / 12)}-year payback period indicates 
                  {Math.round(cashFlowReport.paybackPeriodMonths / 12) <= 3 ? ' rapid' : ' moderate'} 
                  return on investment, making this project 
                  {cashFlowReport.netPresentValue >= 0 ? ' financially attractive' : ' financially challenging'} 
                  for implementation.
                </p>
                <p>
                  <strong>Recommendation:</strong> 
                  {cashFlowReport.netPresentValue >= 0 && cashFlowReport.internalRateOfReturn >= 10 
                    ? 'Proceed with project implementation as the financial metrics indicate positive returns and acceptable risk levels.'
                    : 'Consider revising project parameters or implementation strategy to improve financial viability before proceeding.'}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="report-footer">
          <p>Generated on {new Date().toLocaleDateString()} | Business Value Calculator Report</p>
        </div>
      </div>
    </div>
  );
};

export default BusinessValueReport;
